import React from "react";
import styles from "./InlineParameterField.module.css";
import cn from "classnames";
import { TooltipInteractive } from "src/components/ui-components/Tooltip/TooltipInteractive";
import { AnchorButtonTextOnly } from "src/components/ui-components/ButtonTextOnly/AnchorButtonTextOnly";
import { FormField } from "src/components/ui-components/Input/FormField";
import { Datepicker } from "src/components/datepicker/datepicker";
import { InputField } from "src/components/ui-components/Input/InputField";
import { ParameterEnum } from "src/components/ParameterEnum/ParameterEnum";
import { IconText } from "src/components/ui-components/Icons/IconText";
import { IconNumberSquare } from "src/components/ui-components/Icons/IconNumberSquare";
import { IconList } from "src/components/ui-components/Icons/IconList";
import { IconCalendarBlank } from "src/components/ui-components/Icons/IconCalendarBlank";

import { logger } from "src/utils/logger/browser";
import { useSession } from "src/components/session/session";
import { useDebouncedEffect } from "src/hooks/useDebouncedEffect";
import { isNonEmptyString } from "src/utils/types/types";
import { useLoginUrl } from "src/hooks/useLoginUrl";
import { useSignupUrl } from "src/hooks/useSignupUrl";

import type { Parameter } from "src/utils/parameters/types";

interface Props {
  parameter: Parameter;
  override?: Parameter;
  onChange?: (parameter: Parameter) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  onApply?: () => void;
  disabled?: boolean;

  pageType: "query" | "dashboard";
  editParameterButton?: React.ReactNode;
  hasFocusTooltip?: boolean;
}

export function InlineParameterField(props: Props) {
  const session = useSession();
  const { parameter, disabled = false, hasFocusTooltip = false } = props;

  const loginUrl = useLoginUrl();
  const signupUrl = useSignupUrl();

  const actualField = (
    <div className={cn(disabled && styles.disabled)}>
      <ActualField {...props} />
    </div>
  );

  if (!session) {
    return (
      <TooltipInteractive
        renderInPortal
        closeOn={hasFocusTooltip ? "focus" : undefined}
        position="above-center"
        content={() => (
          <>
            <AnchorButtonTextOnly size="M" theme="primary" href={loginUrl}>
              Sign in
            </AnchorButtonTextOnly>{" "}
            or{" "}
            <AnchorButtonTextOnly size="M" theme="primary" href={signupUrl}>
              sign up
            </AnchorButtonTextOnly>{" "}
            to use parameters
          </>
        )}
      >
        {actualField}
      </TooltipInteractive>
    );
  }

  if (isNonEmptyString(parameter.description)) {
    return (
      <TooltipInteractive
        renderInPortal
        closeOn={hasFocusTooltip ? "focus" : undefined}
        position="above-center"
        content={() => parameter.description}
      >
        {actualField}
      </TooltipInteractive>
    );
  }

  return actualField;
}

const EMPTY_ARRAY: Exclude<Parameter["values"], undefined> = [];

function ActualField(props: Omit<Props, "hasFocusTooltip">) {
  const {
    parameter,
    override,
    onChange,
    disabled = false,
    onKeyDown,
    onApply,
    editParameterButton,
  } = props;

  const { value = "", values = EMPTY_ARRAY } = override ?? {};
  const placeholderValues = parameter.values ?? [];

  useDebouncedEffect(
    { delayMs: 500 },
    disabled ? () => {} : () => onApply?.(),
    [value, values]
  );

  const onString = (value: string) => {
    onChange?.({
      ...parameter,
      value,
    });
  };

  switch (parameter.type) {
    case "text":
      return (
        <InputField
          className={styles.parameterField}
          size="M"
          style="contained"
          inputType="text"
          id={parameter.key}
          prefix={
            <>
              <IconText />
              <label htmlFor={parameter.key}>{parameter.key}</label>
            </>
          }
          suffix={editParameterButton}
          placeholder={parameter.value ?? ""}
          value={value === parameter.default_value ? "" : value}
          onChange={(event) => onString(event.target.value)}
          disabled={disabled || !onChange}
          onKeyDown={onKeyDown}
        />
      );

    case "number":
      return (
        <InputField
          className={styles.parameterField}
          size="M"
          style="contained"
          inputType="number"
          id={parameter.key}
          prefix={
            <>
              <IconNumberSquare />
              <label htmlFor={parameter.key}>{parameter.key}</label>
            </>
          }
          suffix={editParameterButton}
          placeholder={parameter.value ?? ""}
          value={value === parameter.default_value ? "" : value}
          onChange={(event) => onString(event.target.value)}
          disabled={disabled || !onChange}
          onKeyDown={onKeyDown}
        />
      );

    case "datetime":
      return (
        <div>
          <FormField
            size="M"
            style="contained"
            id={parameter.key}
            prefix={
              <>
                <IconCalendarBlank />
                <label htmlFor={parameter.key}>{parameter.key}</label>
              </>
            }
            suffix={editParameterButton}
            disabled={disabled || !onChange}
            renderInput={(props) => (
              <Datepicker
                kind="parameter"
                placeholder={parameter.value}
                value={value}
                onChange={onString}
                disabled={props.disabled}
                required={false}
                onKeyDown={onKeyDown}
                wrapperClassNameOverride={styles.dateParameterField}
                customInput={
                  <input
                    {...props}
                    value={value === parameter.default_value ? "" : value}
                    type="string"
                  />
                }
              />
            )}
          />
        </div>
      );

    case "enum": {
      const { isMultiselect = false } = parameter;

      if (!isMultiselect) {
        return (
          <ParameterEnum
            renderInPortal
            parameter={parameter}
            parameterValues={value === "" ? [parameter.value ?? ""] : [value]}
            onChange={onChange}
            disabled={disabled}
            renderFormField={({ renderButton, disabled }) => (
              <FormField
                size="M"
                style="contained"
                className={cn(props.pageType === "query" && styles.enumField)}
                id={parameter.key}
                prefix={
                  <>
                    <IconList />
                    <label htmlFor={parameter.key}>{parameter.key}</label>
                  </>
                }
                suffix={editParameterButton}
                disabled={disabled}
                renderInput={renderButton}
              />
            )}
            onKeyDown={onKeyDown}
          />
        );
      } else {
        return (
          <ParameterEnum
            renderInPortal
            parameter={parameter}
            parameterValues={values.length > 0 ? values : placeholderValues}
            onChange={onChange}
            disabled={disabled}
            renderFormField={({ renderButton, disabled }) => (
              <FormField
                size="M"
                style="contained"
                className={cn(props.pageType === "query" && styles.enumField)}
                id={parameter.key}
                prefix={
                  <>
                    <IconList />
                    <label htmlFor={parameter.key}>{parameter.key}</label>
                  </>
                }
                suffix={editParameterButton}
                disabled={disabled}
                renderInput={renderButton}
              />
            )}
            onKeyDown={onKeyDown}
          />
        );
      }
    }

    default:
      logger.warn("unsupported parameter type", parameter["type"]);
      return null;
  }
}
